import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal } from 'react-bootstrap-5';

import CreatePropertyForm from './create_property_form';
import SuggestedListingForm from './suggested_listing_form';
import { countryListPropType } from './prop_types';
import { post } from 'helpers/api';
import { pick } from 'lodash';

export function NewPropertyFromScratch({
  brokerSelectOptions,
  defaultBrokerLabel,
  formFields,
  updateFormFields,
  countryList,
  errorMessages,
  gotoPrevStep,
  showCreateDocButton,
  hasErrors,
  hasInsights,
  maxBrokersLimit,
  saving,
  showBrokerSelect,
  onSave,
}) {
  const MODAL_STEPS = {
    NEW_LISTING: 'new_listing',
    SUGGESTED_LISTING: 'suggested_listing',
    LISTING_NOT_FOUND: 'listing_not_found'
  };

  // Find the default broker from the options by label name and pre-select it
  const getInitialBroker = () => {
    if (defaultBrokerLabel && brokerSelectOptions) {
      const defaultBroker = brokerSelectOptions.find(broker => broker.label === defaultBrokerLabel);
      if (defaultBroker) {
        return [defaultBroker];
      }
    }
    return [{}];
  };

  const [brokers, setBrokers] = useState(getInitialBroker);

  const [modalStatus, setModalStatus] = useState(hasInsights ? MODAL_STEPS.NEW_LISTING : MODAL_STEPS.LISTING_NOT_FOUND);
  const [fullAddress, setFullAddress] = useState('');
  const [suggestedAddresses, setSuggestedAddresses] = useState([]);
  const [checkingInsights, setCheckingInsights] = useState(false);

  function updateForm(changes) {
    if (changes.country && !changes.state) {
      changes.state = changes.country.countryStates[0]?.code || '';
      changes.zip = '';
    }

    for (const key in changes) {
      updateFormFields(key, changes[key]);
    }
  }

  const buildFullAddress = ({ address, city, state, zip }) => `${address}, ${city}, ${state} ${zip || ''}`;

  const handlePropertySearch = async () => {
    setCheckingInsights(true);
    const body = {
      insights_property: pick(formFields, ['address', 'city', 'state', 'zip']),
      only: ['address', 'city', 'state', 'zip', 'propertyId']
    };
    const insightResponse = await post('/insights_properties/search', body);
    const builtAddress = buildFullAddress(formFields);
    setFullAddress(builtAddress);

    if (insightResponse['total'] === 0) {
      setModalStatus(MODAL_STEPS.LISTING_NOT_FOUND);
      createPropertyListing();
    } else {
      const records = insightResponse['hits'].map((record) => {
        return { fullAddress: buildFullAddress(record), id: record.propertyId };
      });
      setSuggestedAddresses(records);
      setModalStatus(MODAL_STEPS.SUGGESTED_LISTING);
    }
    setCheckingInsights(false);
  };

  const returnFromAddressCorrection = () => {
    setModalStatus(MODAL_STEPS.NEW_LISTING);
  };

  const isNewListing = modalStatus === MODAL_STEPS.NEW_LISTING;
  const isListingNotFound = modalStatus === MODAL_STEPS.LISTING_NOT_FOUND;
  const isSuggestedListing = modalStatus === MODAL_STEPS.SUGGESTED_LISTING;

  const researchPropertyParams = {
    ...pick(formFields, ['address', 'city', 'state', 'zip', 'county']),
    country_id: formFields.country?.id,
    property_type_id: formFields.property_type_id,
  };
  const propertyParams = {
    sale_checked: formFields.sale_or_lease === 'sale',
    lease_checked: formFields.sale_or_lease === 'lease',
  };

  const aiContentFlags = {
    generateSaleDescription: formFields.generate_sale_description !== false,
    generateSaleBullets: formFields.generate_sale_bullets !== false,
    generateLeaseDescription: formFields.generate_lease_description !== false,
    generateLeaseBullets: formFields.generate_lease_bullets !== false
  };

  const createPropertyListing = (extraParams = {}) => {
    onSave({
      brokerIds: brokers.map(broker => broker.value),
      propertyParams,
      researchPropertyParams,
      aiContentFlags,
      ...extraParams
    });
  };

  const createPropertyListingWithDocument = () => {
    createPropertyListing({ createDocument: true });
  };

  const suggestedPropertyListing = (params) => {
    onSave({
      brokerIds: brokers.map(broker => broker.value),
      propertyParams,
      researchPropertyParams,
      ...params
    });
  };

  return (
    // NOTE: `key` forces a remount when modalStatus changes, resetting its state
    <Modal.Body className="p-8">
      {(isNewListing || isListingNotFound) && (
        <CreatePropertyForm
          addressNotFound={fullAddress}
          brokers={brokers}
          countryList={countryList}
          errorMessages={errorMessages}
          formFields={formFields}
          gotoPrevStep={gotoPrevStep}
          showCreateDocButton={showCreateDocButton}
          hasErrors={hasErrors}
          hasInsights={hasInsights}
          key={modalStatus}
          listingNotFound={isListingNotFound}
          maxBrokersLimit={maxBrokersLimit}
          newListing={isNewListing}
          saving={saving || checkingInsights}
          showBrokerSelect={showBrokerSelect}
          onBrokerChange={setBrokers}
          onChange={updateForm}
          onFormSubmit={isNewListing ? handlePropertySearch : createPropertyListing}
          onFormSubmitWithDocument={createPropertyListingWithDocument}
        />
      )}
      {isSuggestedListing && (
        <SuggestedListingForm
          backButtonClicked={returnFromAddressCorrection}
          brokers={brokers}
          errorMessages={errorMessages}
          fullAddress={fullAddress}
          hasErrors={hasErrors}
          maxBrokersLimit={maxBrokersLimit}
          saving={saving}
          showBrokerSelect={showBrokerSelect}
          suggestedAddresses={suggestedAddresses}
          suggestedPropertyListing={suggestedPropertyListing}
          userEnteredFields={researchPropertyParams}
          onBrokerChange={setBrokers}
        />
      )}
    </Modal.Body>
  );
}

NewPropertyFromScratch.propTypes = {
  brokerSelectOptions: PropTypes.array,
  countryList: countryListPropType.isRequired,
  defaultBrokerLabel: PropTypes.string,
  errorMessages: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array
  ]),
  formFields: PropTypes.shape({
    address: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.object,
    country_id: PropTypes.number,
    county: PropTypes.string,
    property_type_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sale_or_lease: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    generate_sale_description: PropTypes.bool,
    generate_sale_bullets: PropTypes.bool,
    generate_lease_description: PropTypes.bool,
    generate_lease_bullets: PropTypes.bool
  }),
  gotoPrevStep: PropTypes.func,
  showCreateDocButton: PropTypes.bool,
  hasErrors: PropTypes.bool.isRequired,
  hasInsights: PropTypes.bool.isRequired,
  maxBrokersLimit: PropTypes.number.isRequired,
  saving: PropTypes.bool.isRequired,
  showBrokerSelect: PropTypes.bool.isRequired,
  updateFormFields: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};


const mapStateToProps = state => ({
  formFields: state.newPropertyFromScratchLocation || {},
  brokerSelectOptions: state.brokerSelectOptions || []
});

const mapDispatchToProps = dispatch => ({
  updateFormFields: (fieldName, value) => {
    return dispatch({ type: `change_${fieldName}`, [fieldName]: value });
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(NewPropertyFromScratch);
